import { migrateIncidentTypeFilters } from "@/lib/utils/matters/incident-classification";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

const itemMapping = {
  contact: {
    routeApi: "/_auth/contacts/",
    navigateFrom: "/contacts",
  },
  matter: {
    routeApi: "/_auth/matters/",
    navigateFrom: "/matters",
  },
  closedMatters: {
    routeApi: "/_auth/reports/closed-matters",
    navigateFrom: "/reports/closed-matters",
  },
  myMatters: {
    routeApi: "/_auth/matters/my-matters",
    navigateFrom: "/matters/my-matters",
  },
  intakeOpportunities: {
    routeApi: "/_auth/opportunities/",
    navigateFrom: "/opportunities",
  },
  lead: {
    routeApi: "/_auth/leads/",
    navigateFrom: "/leads",
  },
  referral: {
    routeApi: "/_auth/referrals/",
    navigateFrom: "/referrals",
  },
} as const;

const SEARCH_OBJECT_STORAGE_PREFIX = "lawworks:table-filters:";

type SearchObject = { [key: string]: string[] };

const readStoredSearchObject = (storageKey: string): SearchObject => {
  if (typeof window === "undefined") return {};

  try {
    const stored = window.localStorage.getItem(storageKey);
    if (!stored) return {};

    const parsed: unknown = JSON.parse(stored);
    if (
      !parsed ||
      typeof parsed !== "object" ||
      Array.isArray(parsed) ||
      !Object.values(parsed).every(
        (value) =>
          Array.isArray(value) &&
          value.every((item) => typeof item === "string"),
      )
    ) {
      return {};
    }

    return parsed as SearchObject;
  } catch {
    return {};
  }
};

export const useSearchObject = ({
  syncWithSearchParams = false,
  itemType = "contact",
  defaultSearchObject,
  persistSearchObject = false,
}: {
  syncWithSearchParams?: boolean;
  itemType?: keyof typeof itemMapping;
  defaultSearchObject?: SearchObject;
  /** Restores filters for this table when revisiting it without URL search params. */
  persistSearchObject?: boolean;
} = {}) => {
  let routeApi;
  let rawSearchParams = useMemo<SearchObject>(() => ({}), []);
  if (syncWithSearchParams) {
    routeApi = getRouteApi(itemMapping[itemType].routeApi);
    rawSearchParams = routeApi.useSearch();
  }
  const searchParams = useMemo(
    () =>
      itemType === "contact"
        ? rawSearchParams
        : migrateIncidentTypeFilters(rawSearchParams),
    [itemType, rawSearchParams],
  );
  const navigate = useNavigate({ from: itemMapping[itemType].navigateFrom });
  const storageKey = `${SEARCH_OBJECT_STORAGE_PREFIX}${itemType}`;
  const initialSearchObject = useMemo(() => {
    if (!syncWithSearchParams)
      return itemType === "contact"
        ? (defaultSearchObject ?? {})
        : migrateIncidentTypeFilters(defaultSearchObject ?? {});
    if (Object.keys(searchParams).length > 0) return searchParams;
    return persistSearchObject
      ? itemType === "contact"
        ? readStoredSearchObject(storageKey)
        : migrateIncidentTypeFilters(readStoredSearchObject(storageKey))
      : searchParams;
  }, [
    defaultSearchObject,
    itemType,
    persistSearchObject,
    searchParams,
    storageKey,
    syncWithSearchParams,
  ]);

  const [searchObject, setSearchObject] =
    useState<SearchObject>(initialSearchObject);

  const persistSearch = useCallback(
    (nextSearchObject: SearchObject) => {
      if (!persistSearchObject || typeof window === "undefined") return;

      if (Object.keys(nextSearchObject).length === 0) {
        window.localStorage.removeItem(storageKey);
        return;
      }

      window.localStorage.setItem(storageKey, JSON.stringify(nextSearchObject));
    },
    [persistSearchObject, storageKey],
  );

  useEffect(() => {
    if (
      syncWithSearchParams &&
      (persistSearchObject || rawSearchParams !== searchParams) &&
      (Object.keys(rawSearchParams).length === 0 ||
        rawSearchParams !== searchParams) &&
      Object.keys(initialSearchObject).length > 0
    ) {
      persistSearch(initialSearchObject);
      void navigate({ search: initialSearchObject, replace: true });
    }
  }, [
    initialSearchObject,
    rawSearchParams,
    persistSearch,
    navigate,
    persistSearchObject,
    searchParams,
    syncWithSearchParams,
  ]);

  const resetSearchObject = useCallback(async () => {
    const nextSearchObject = syncWithSearchParams
      ? searchParams
      : (defaultSearchObject ?? {});
    setSearchObject(nextSearchObject);
    persistSearch(nextSearchObject);
    if (syncWithSearchParams) {
      await navigate({
        search: nextSearchObject,
        replace: true,
      });
    }
  }, [
    defaultSearchObject,
    navigate,
    persistSearch,
    searchParams,
    syncWithSearchParams,
  ]);

  const clearSearchObject = useCallback(async () => {
    setSearchObject({});
    persistSearch({});
    if (syncWithSearchParams) {
      await navigate({
        search: {},
        replace: true,
      });
    }
  }, [navigate, persistSearch, syncWithSearchParams]);

  const replaceSearchObject = useCallback(
    async (nextSearchObject: { [key: string]: string[] }) => {
      setSearchObject(nextSearchObject);
      persistSearch(nextSearchObject);
      if (syncWithSearchParams) {
        await navigate({
          search: nextSearchObject,
          replace: true,
        });
      }
    },
    [navigate, persistSearch, syncWithSearchParams],
  );

  const addSearchObjectValue = useCallback(
    async (key: string, value: string, replace = false) => {
      if (replace) {
        await replaceSearchObject({ ...searchObject, [key]: [value] });
        return;
      }
      let newSearchObject = {};
      setSearchObject((prev) => {
        if (Object.keys(prev).includes(key)) {
          if (!prev[key].includes(value)) {
            newSearchObject = {
              ...prev,
              [key]: [...prev[key], value],
            };
            return newSearchObject;
            // If the key/val pair already exists we don't want to add it again
          } else {
            toast.error("This search filter already exists", {
              id: "search-filter-already-exists",
            });
            newSearchObject = prev;
            return prev;
          }
        } else {
          newSearchObject = value
            ? {
                ...prev,
                [key]: [value],
              }
            : {};
        }
        return newSearchObject;
      });
      if (syncWithSearchParams) {
        persistSearch(newSearchObject);
        await navigate({
          search: newSearchObject,
          replace: true,
        });
      }
    },
    [
      navigate,
      persistSearch,
      syncWithSearchParams,
      replaceSearchObject,
      searchObject,
    ],
  );

  const removeSearchObjectValue = useCallback(
    async (key: string, value: string) => {
      let newSearchObject = {};
      setSearchObject((prev) => {
        const newValueArray = prev[key].filter((val) => val !== value);
        if (!newValueArray.length) {
          newSearchObject = Object.fromEntries(
            Object.entries(prev).filter(([searchKey]) => searchKey !== key),
          );
        } else {
          newSearchObject = {
            ...prev,
            [key]: newValueArray,
          };
        }
        return newSearchObject;
      });
      if (syncWithSearchParams) {
        persistSearch(newSearchObject);
        await navigate({
          search: newSearchObject,
          replace: true,
        });
      }
    },
    [navigate, persistSearch, syncWithSearchParams],
  );
  // when navigating to a different page, we want to update the search object
  useEffect(() => {
    if (syncWithSearchParams) {
      setSearchObject(
        Object.keys(searchParams).length ? searchParams : initialSearchObject,
      );
      if (Object.keys(searchParams).length > 0) {
        persistSearch(searchParams);
      }
    }
  }, [initialSearchObject, persistSearch, searchParams, syncWithSearchParams]);

  return {
    searchObject,
    addSearchObjectValue,
    removeSearchObjectValue,
    clearSearchObject,
    replaceSearchObject,
    resetSearchObject,
  };
};
